import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { adminService } from '../services/adminService';
import {
  Company,
  CreateUserRequest,
  UpdateUserRequest,
  SystemSettings,
  UsersFilters,
  LogsFilters,
} from '../types';
import { LoadingState } from '../../../core/types';

export const useAdmin = () => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);

  // Company queries
  const {
    data: company,
    isLoading: isLoadingCompany,
    error: companyError,
    refetch: refetchCompany,
  } = useQuery({
    queryKey: ['admin', 'company'],
    queryFn: () => adminService.getCompany(),
    staleTime: 5 * 60 * 1000,
  });

  // Settings queries
  const {
    data: settings,
    isLoading: isLoadingSettings,
    error: settingsError,
    refetch: refetchSettings,
  } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => adminService.getSettings(),
    staleTime: 5 * 60 * 1000,
  });

  // Users queries
  const {
    data: users,
    isLoading: isLoadingUsers,
    error: usersError,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminService.getUsers(),
    staleTime: 5 * 60 * 1000,
  });

  // Logs queries
  const {
    data: logs,
    isLoading: isLoadingLogs,
    error: logsError,
    refetch: refetchLogs,
  } = useQuery({
    queryKey: ['admin', 'logs'],
    queryFn: () => adminService.getLogs(),
    staleTime: 1 * 60 * 1000, // Logs refresh more frequently
  });

  const getUsers = useCallback(async (filters?: UsersFilters) => {
    try {
      setLoading('loading');
      setError(null);
      const response = await adminService.getUsers(filters);
      setLoading('success');
      return response;
    } catch (err: any) {
      setLoading('error');
      setError(err?.response?.data?.error || 'Kullanıcılar yüklenirken hata oluştu');
      throw err;
    }
  }, []);

  // Logs queries
  const getLogs = useCallback(async (filters?: LogsFilters) => {
    try {
      setLoading('loading');
      setError(null);
      const response = await adminService.getLogs(filters);
      setLoading('success');
      return response;
    } catch (err: any) {
      setLoading('error');
      setError(err?.response?.data?.error || 'Sistem logları yüklenirken hata oluştu');
      throw err;
    }
  }, []);

  // Company mutations
  const updateCompanyMutation = useMutation({
    mutationFn: (companyData: Partial<Company>) => adminService.updateCompany(companyData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'company'] });
      toast.success('Şirket bilgileri başarıyla güncellendi');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Şirket bilgileri güncellenirken hata oluştu';
      toast.error(errorMessage);
    },
  });

  // User mutations
  const createUserMutation = useMutation({
    mutationFn: (userData: CreateUserRequest) => adminService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Kullanıcı başarıyla oluşturuldu');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Kullanıcı oluşturulurken hata oluştu';
      toast.error(errorMessage);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      adminService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Kullanıcı başarıyla güncellendi');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Kullanıcı güncellenirken hata oluştu';
      toast.error(errorMessage);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Kullanıcı başarıyla silindi');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Kullanıcı silinirken hata oluştu';
      toast.error(errorMessage);
    },
  });

  // Settings mutations
  const updateSettingsMutation = useMutation({
    mutationFn: (settingsData: Partial<SystemSettings>) => adminService.updateSettings(settingsData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
      toast.success('Sistem ayarları başarıyla güncellendi');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Sistem ayarları güncellenirken hata oluştu';
      toast.error(errorMessage);
    },
  });

  return {
    // Data
    company,
    settings,
    users,
    logs,
    
    // Loading states
    loading,
    isLoadingCompany,
    isLoadingSettings,
    isLoadingUsers,
    isLoadingLogs,
    isUpdatingCompany: updateCompanyMutation.isPending,
    isCreatingUser: createUserMutation.isPending,
    isUpdatingUser: updateUserMutation.isPending,
    isDeletingUser: deleteUserMutation.isPending,
    isUpdatingSettings: updateSettingsMutation.isPending,

    // Error states
    error: error || companyError?.message || settingsError?.message || usersError?.message || logsError?.message,
    companyError,
    settingsError,
    usersError,
    logsError,

    // Actions - Async wrapper'lar ile Promise<void> döndürüyoruz
    updateCompany: async (data: Partial<Company>) => {
      await updateCompanyMutation.mutateAsync(data);
    },
    createUser: async (data: CreateUserRequest) => {
      await createUserMutation.mutateAsync(data);
    },
    updateUser: async (id: string, data: UpdateUserRequest) => {
      await updateUserMutation.mutateAsync({ id, data });
    },
    deleteUser: async (id: string) => {
      await deleteUserMutation.mutateAsync(id);
    },
    updateSettings: async (data: Partial<SystemSettings>) => {
      await updateSettingsMutation.mutateAsync(data);
    },
    getUsers,
    getLogs,
    refetchCompany,
    refetchSettings,
    refetchUsers,
    refetchLogs,
  };
}; 